import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap border font-medium transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:ring-ring/50 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        neutral: 'border-foreground bg-neutral/5 text-neutral',
        primary: 'border-primary bg-primary/5 text-primary',
        muted: 'border-muted-foreground/50 bg-muted/5 text-muted-foreground',
        teal: 'border-teal bg-teal/5 text-teal',
        info: 'border-info bg-info/5 text-info',
        success: 'border-success bg-success/5 text-success',
        warning: 'border-warning bg-warning/5 text-warning',
        error: 'border-error bg-error/5 text-error',
      },
      size: {
        sm: 'px-2 py-1 text-xs',
        md: 'px-2.5 py-0.5 text-xs font-semibold',
        lg: 'px-3 py-1 text-sm font-semibold',
      },
      radius: {
        full: 'rounded-full',
        md: 'rounded-md',
        sm: 'rounded-sm',
        none: 'rounded-none',
      },
      interactive: {
        true: 'cursor-pointer',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'neutral',
        interactive: true,
        className:
          'hover:bg-neutral/10 hover:text-neutral-hover hover:border-neutral-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-ring',
      },
      {
        variant: 'primary',
        interactive: true,
        className:
          'hover:bg-primary/10 hover:text-primary-hover hover:border-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-ring',
      },
      {
        variant: 'muted',
        interactive: true,
        className:
          'hover:bg-muted/10 hover:text-muted-foreground hover:border-muted-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-muted-ring',
      },
      {
        variant: 'teal',
        interactive: true,
        className:
          'hover:bg-teal/10 hover:text-teal-hover hover:border-teal-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-ring',
      },
      {
        variant: 'info',
        interactive: true,
        className:
          'hover:bg-info/10 hover:text-info-hover hover:border-info-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-ring',
      },
      {
        variant: 'success',
        interactive: true,
        className:
          'hover:bg-success/10 hover:text-success-hover hover:border-success-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success-ring',
      },
      {
        variant: 'warning',
        interactive: true,
        className:
          'hover:bg-warning/10 hover:text-warning-hover hover:border-warning-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning-ring',
      },
      {
        variant: 'error',
        interactive: true,
        className:
          'hover:bg-error/10 hover:text-error-hover hover:border-error-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error-ring',
      },
    ],
    defaultVariants: {
      variant: 'neutral',
      size: 'md',
      radius: 'full',
      interactive: false,
    },
  }
);

type BadgeProps = React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    noBorder?: boolean;
    noBackground?: boolean;
  };

function Badge({
  className,
  variant = 'neutral',
  size,
  radius,
  interactive,
  asChild = false,
  noBorder = false,
  noBackground = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(
        badgeVariants({ variant, size, radius, interactive }),
        noBorder && 'border-0',
        noBackground && 'bg-transparent',
        className
      )}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
export type { BadgeProps };
